import { Component, EventEmitter, OnInit } from '@angular/core';
import { DwButtonCommand, DwButtonConfig, DwMessage, DwMessageSeverity, DwModalConfig, DwModalHandler, DwModalResult } from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppMetaData, CalendarReferenceEntity } from '../../../../meta-data/app-meta-data.service';
import { CalendarConfig } from '../../models/calendar-config.model';
import { CalendarDTO } from '../../models/calendar.model';
import { CalendarRepositoryService } from '../../services/calendar-repository.service';

export interface CalendarSettingsModalData {
  calendarReference: CalendarReferenceEntity;
  allCalendars: CalendarDTO[];
}

@Component({
  selector: 'app-calendar-settings-modal',
  templateUrl: './calendar-settings-modal.component.html',
  styleUrls: ['./calendar-settings-modal.component.scss']
})
export class CalendarSettingsModalComponent implements OnInit, DwModalHandler {
  modalConfig: DwModalConfig<CalendarSettingsModalData>;
  closeModal = new EventEmitter<DwModalResult<CalendarReferenceEntity>>();

  calendarReference: CalendarReferenceEntity;
  calendarConfig: CalendarConfig = { AdditionalCalendars: [] };
  allCalendars: CalendarDTO[] = [];
  alternateCalendars: CalendarDTO[] = [];
  message: DwMessage;
  removingAccess = false;

  constructor(private calendarRepositoryService: CalendarRepositoryService) { }

  ngOnInit(): void {
    const sourceReference = this.modalConfig.data.calendarReference;
    const sourceConfig = sourceReference.CalendarConfigJSON as any;

    this.calendarReference = { ...sourceReference };
    this.calendarConfig = {
      AdditionalCalendars: [...(sourceConfig?.AdditionalCalendars || [])]
    };
    this.allCalendars = [...(this.modalConfig.data.allCalendars || [])];
    this.setupAdditionalCalendars();
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult<CalendarReferenceEntity>> {
    if (buttonConfig.command !== DwButtonCommand.saveAndClose) {
      return of({
        cancelled: true,
        closeModal: true,
        buttonCommand: buttonConfig.command
      } as DwModalResult<CalendarReferenceEntity>);
    }

    if (!this.calendarReference?.MainCalendarKey) {
      this.message = {
        messageBody: 'Select a booking calendar before saving.',
        severity: DwMessageSeverity.error
      };

      return of({ closeModal: false } as DwModalResult<CalendarReferenceEntity>);
    }

    const calendarReferenceUpdate = AppMetaData.CalendarReference.CreateEntity();
    calendarReferenceUpdate.CalendarReferenceId = this.calendarReference.CalendarReferenceId;
    calendarReferenceUpdate.MainCalendarKey = this.calendarReference.MainCalendarKey;
    calendarReferenceUpdate.MainCalendarTimezone = this.calendarReference.MainCalendarTimezone;
    calendarReferenceUpdate.CalendarConfigJSON = this.calendarConfig as any;

    return this.calendarRepositoryService.saveCalendarRef(calendarReferenceUpdate)
      .pipe(map(calendarReference => ({
        cancelled: false,
        closeModal: true,
        buttonCommand: buttonConfig.command,
        data: calendarReference
      } as DwModalResult<CalendarReferenceEntity>)));
  }

  setupAdditionalCalendars(): void {
    if (!this.calendarReference || !this.allCalendars.length) {
      this.alternateCalendars = [];
      return;
    }

    const mainCalendar = this.allCalendars.find(calendar => calendar.Id === this.calendarReference.MainCalendarKey);
    this.calendarReference.MainCalendarTimezone = mainCalendar?.TimeZone;
    this.alternateCalendars = this.allCalendars.filter(calendar => calendar.Id !== this.calendarReference.MainCalendarKey);
    this.calendarConfig.AdditionalCalendars = this.calendarConfig.AdditionalCalendars
      .filter(calendarId => this.alternateCalendars.some(calendar => calendar.Id === calendarId));
  }

  removeAccess(): void {
    this.removingAccess = true;
    this.message = null;

    this.calendarRepositoryService.revokeCalendarDelegation()
      .subscribe(response => {
        this.removingAccess = false;

        if (!response.IsSuccess) {
          this.message = {
            messageBody: `Calendar delegation could not be removed: ${response.ErrorMessage}`,
            severity: DwMessageSeverity.error
          };
          return;
        }

        this.closeModal.emit({
          cancelled: false,
          closeModal: true,
          data: null
        } as DwModalResult<CalendarReferenceEntity>);
      });
  }
}
